import { action, computed, makeObservable, observable } from 'mobx'

import { IRemoteTileset } from '~/client/graph/types.generated'
import RemoteTilesetsStore from '~/client/src/shared/stores/domain/RemoteTilesets.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import MapViewItemsSetupStore from './MapViewItemsSetup.store'

export default class RemoteTilesetsSetupStore {
  @observable public selectedRemoteTilesetId: string = null
  @observable public deletableRemoteTileset: IRemoteTileset = null

  public constructor(
    private readonly remoteTilesetsStore: RemoteTilesetsStore,
    private readonly state: InitialState,
    private readonly mapViewItemsSetupStore: MapViewItemsSetupStore,
  ) {
    makeObservable(this)
  }

  public async uploadRemoteTileset(
    remoteTileset: IRemoteTileset,
  ): Promise<void> {
    return await this.remoteTilesetsStore.uploadRemoteTileset(remoteTileset)
  }

  @computed
  public get selectedRemoteTileset(): IRemoteTileset {
    return this.state.remoteTilesets.get(this.selectedRemoteTilesetId)
  }

  @action.bound
  public showDeleteDialog(): void {
    this.showDeleteConfirmationDialogByRemoteTileset(this.selectedRemoteTileset)
  }

  @action.bound
  public async selectRemoteTilesetById(
    id: string,
    shouldSaveSelectedItem: boolean = true,
  ): Promise<void> {
    const remoteTileset = this.state.remoteTilesets.get(id)
    this.selectRemoteTileset(remoteTileset, shouldSaveSelectedItem)
  }

  @action.bound
  public async selectRemoteTileset(
    remoteTileset: IRemoteTileset,
    shouldSaveSelectedItem: boolean = true,
  ): Promise<void> {
    if (!remoteTileset) {
      return
    }

    if (shouldSaveSelectedItem) {
      await this.mapViewItemsSetupStore.saveSelectedMapViewItem()
    }

    this.deselectRemoteTileset()
    this.selectedRemoteTilesetId = remoteTileset.id
  }

  @action.bound
  public deselectRemoteTileset(): void {
    this.selectedRemoteTilesetId = null
  }

  @action.bound
  public showDeleteConfirmationDialogByRemoteTileset(
    remoteTileset: IRemoteTileset,
  ): void {
    this.deletableRemoteTileset = remoteTileset
  }

  @action.bound
  public showDeleteConfirmationDialogByRemoteTilesetId(
    remoteTilesetId: string,
  ): void {
    this.deletableRemoteTileset = Array.from(
      this.state.remoteTilesets.values(),
    ).find(s => s.id === remoteTilesetId)
  }

  @action.bound
  public hideRemoteTilesetDeleteConfirmDialog(): void {
    this.deletableRemoteTileset = null
  }

  @action.bound
  public applyRemoteTilesetDeleteConfirmDialog(): void {
    if (!this.deletableRemoteTileset) {
      return
    }

    this.deselectRemoteTileset()
    this.remoteTilesetsStore.removeRemoteTileset(this.deletableRemoteTileset.id)

    this.hideRemoteTilesetDeleteConfirmDialog()
  }
}
